import React from 'react';
import Navbar from './common/Layout/Headers/Navbar.js';
import Sidebar from './common/Layout/Sidebar/Sidebar.js';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  unstable_HistoryRouter as HistoryRouter,
} from 'react-router-dom';
import { history } from './app/history.js';
import Loader from './common/Loader';
import DashboardPage from './features/dashboard/components/dashboardPages';
import BbsOptimizationPage from './features/bbsOptimization/components/createBbs/index.js';
import Login from './features/login/index.js';
import RequestApproval from './features/requestApproval/components/requestApproval/index.js';
import QuarterlyInputPage from './features/quarterlyInput/components/quarterlyInputPage/index.js';
import QuarterlyInput from '@features/quarterlyInput/index.js';
import DashboardCalculations from './features/dashboard/components/dashboardCalculations/index.js';
import { MsalProvider } from '@azure/msal-react';
import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { useSelector } from 'react-redux';
import { getLoggedInUser } from 'utilities/helpers';
import ViewRequestApproval from './features/requestApproval/components/viewRequestApproval/index.js';
import ManagerApproveReject from './features/dashboard/components/managerApproveReject/index.js';
import DashboardCreate from './features/dashboard/components/dashboardCreate/index.js';
import UserApproveReject from './features/requestApproval/components/userApproveReject/index.js';
import ViewBBS from './features/dashboard/components/viewBBS/index.js';
import LeaderShipDashboard from './features/dashboard/components/leaderShipDashboard/index.js';
import ViewerDashboard from './features/viewer/pages/Dashboard.jsx';
import ToolAdherence from '@features/viewer/pages/ToolAdherence.jsx';
import AdvanceSearch from '@features/advanceSearch/index.jsx';
import TrainingVideo from '@features/trainingGuide/pages/TrainingVideo.jsx';
import TrainingManual from '@features/trainingGuide/pages/TrainingManual.jsx';

const configuration = {
  auth: {
    clientId: process.env.REACT_APP_CLIENT_ID,
    authority: process.env.REACT_APP_AUTHORITY,
    redirectUri: process.env.REACT_APP_URL,
  },
  cache: {
    cacheLocation: 'localStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    windowHashTimeout: 60000,
    iframeHashTimeout: 60000,
    loadFrameTimeout: 0,
    asyncPopups: false,
  },
};
const pca = new PublicClientApplication(configuration);
pca.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS) {
    pca.setActiveAccount(event.payload.account);
  }
});

const roleBasedNavigation = (id) => {
  switch (id) {
    case 'REQUESTER':
      return '/dashboard';
    case 'MANAGER':
      return '/dashboard';
    case 'VIEWER':
      return '/viewer/dashboard';
    default:
      return '/';
  }
};

const PrivateRoute = ({ children }) => {
  let location = useLocation();

  const { loading } = useSelector((state) => state.login);
  const { loggedIn } = getLoggedInUser();

  if (loading) {
    return <Loader />;
  }

  if (!loggedIn) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  return children;
};

const AuthChecker = ({ children }) => {
  let location = useLocation();
  const { loggedIn, user } = getLoggedInUser();
  let goTo = roleBasedNavigation(user?.Role?.role);

  if (loggedIn) {
    return <Navigate to={goTo} state={{ from: location }} />;
  }

  return children;
};

function App() {
  return (
    <MsalProvider instance={pca}>
      <HistoryRouter history={history}>
        <AppContent />
      </HistoryRouter>
    </MsalProvider>
  );
}

function AppContent() {
  const { user } = getLoggedInUser();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  // console.log('isLoginPage@@@@@@@@@: ', isLoginPage);
  // console.log('user@@@@@@@@@: ', user);
  let goTo = location.state?.from?.pathname
    ? location.state?.from?.pathname
    : user?.Role?.role
      ? roleBasedNavigation(user?.Role?.role || '')
      : '/login';

  return (
    <>
      {!isLoginPage && <Navbar />}
      <div style={{ display: 'flex' }}>
        {!isLoginPage && user?.Role?.role !== 'VIEWER' ? <Sidebar /> : null}

        <Routes>
          <Route path="/" element={<Navigate replace to={goTo} />} />

          <Route
            path="login"
            element={
              <AuthChecker>
                <MsalProvider instance={pca}>
                  <Login />
                </MsalProvider>
              </AuthChecker>
            }
          />
          <Route
            path="dashboard"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <DashboardPage />
                </MsalProvider>
              </PrivateRoute>
            }
          />
          <Route
            path="request-approval"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <RequestApproval />
                </MsalProvider>
              </PrivateRoute>
            }
          />
          <Route
            path="bbs-optimization"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <BbsOptimizationPage />
                </MsalProvider>
              </PrivateRoute>
            }
          />
          <Route
            path="quarterly-input"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <QuarterlyInputPage />
                </MsalProvider>
              </PrivateRoute>
            }
          />
          <Route
            path="request-approval/:projectCode"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <ViewRequestApproval />
                </MsalProvider>
              </PrivateRoute>
            }
          />

          <Route
            path="approve-reject/:id"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <ManagerApproveReject />
                </MsalProvider>
              </PrivateRoute>
            }
          />

          <Route
            path="dashboard/:id"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <DashboardCreate />
                </MsalProvider>
              </PrivateRoute>
            }
          />

          <Route
            path="view-BBS/:id"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <ViewBBS />
                </MsalProvider>
              </PrivateRoute>
            }
          />

          <Route
            path="user-approvereject/:id"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <UserApproveReject />
                </MsalProvider>
              </PrivateRoute>
            }
          />

          <Route
            path="leadership-dashboard"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <LeaderShipDashboard />
                </MsalProvider>
              </PrivateRoute>
            }
          />

          <Route
            path="dashboard-calculation"
            element={<DashboardCalculations />}
          />
          <Route
            path="viewer/dashboard"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <ViewerDashboard />
                </MsalProvider>
              </PrivateRoute>
            }
          />
          <Route
            path="viewer/tool-adherence"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <ToolAdherence />
                </MsalProvider>
              </PrivateRoute>
            }
          />
          <Route
            path="training-video"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <TrainingVideo />
                </MsalProvider>
              </PrivateRoute>
            }
          />
          <Route
            path="training-manual"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <TrainingManual />
                </MsalProvider>
              </PrivateRoute>
            }
          />
          {/* <Route
            path="advance-search"
            element={
              <PrivateRoute>
                <MsalProvider instance={pca}>
                  <AdvanceSearch />
                </MsalProvider>
              </PrivateRoute>
            }
          /> */}
        </Routes>
      </div>
    </>
  );
}

export default App;
